/* eslint-disable no-undef */
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import useHasRole from "hooks/useHasRole";

// @material-ui/core components
import { Tooltip } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Divider from "@material-ui/core/Divider";
import Grow from "@material-ui/core/Grow";
import Hidden from "@material-ui/core/Hidden";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import { makeStyles } from "@material-ui/core/styles";
import { useClientConfig } from "hooks/useClientConfig";

import { Person, Menu, BugReport } from "@mui/icons-material";

// core components
import Button from "components/Forms/CustomButtons/Button.js";
import Modal from "components/Modals/Modal/Modal.js";

import styles from "assets/jss/material-dashboard-pro-react/components/adminNavbarLinksStyle.js";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useClientConfig as useClientConfigApi } from "hooks/useClientConfig";

import useLogin from "hooks/useLogin";
import { useClientConfig as useClientConfigBlob } from "services/blob/get-client-config";
import NotificationPopup from "./components/Notifications/index";
import { CustomDrawerConfigs } from "../UI/SideDrawers/SideDrawerConfigs";
import { CustomDrawerReport } from "../UI/SideDrawers/SideDrawerReport";
import {
  useDrawerOpen,
  useDrawerReporteOpen,
} from "store/user-settings/user-settings-store";
import { useReportsContext } from "store/reports/reportsContext";
import Reports from "./components/Reports";

const useStyles = makeStyles(styles);

export default function HeaderLinks(props) {
  const { isLoadingReport, getReportsList } = useReportsContext();
  let navigate = useNavigate();
  const { drawerOpen, setDrawerOpen } = useDrawerOpen();
  const { drawerReporteOpen, setDrawerReporteOpen } = useDrawerReporteOpen();
  const [openConfirmModal, setOpenConfirmModal] = React.useState(null);
  const { hasRealmRole } = useHasRole();

  const { logout } = useLogin();
  const { t } = useTranslation("navbar-links");
  const { isItemActive } = useClientConfigApi();

  const {
    data: clientConfig,
    isLoading: isClientConfigLoading,
  } = useClientConfigBlob();

  useEffect(() => {
    getReportsList();
  }, []);

  const handleOpenConfirmModal = () => {
    setOpenConfirmModal(
      <Modal onConfirm={logout} onDenied={hideConfirmModal} />,
    );
  };

  const [openProfile, setOpenProfile] = React.useState(null);
  const handleClickProfile = (event) => {
    if (openProfile && openProfile.contains(event.target)) {
      setOpenProfile(null);
    } else {
      setOpenProfile(event.currentTarget);
    }
  };

  const handleCloseProfile = () => {
    setOpenProfile(null);
  };

  const navigateToProfile = () => {
    navigate("/app/profile");
    handleCloseProfile();
  };

  const hideConfirmModal = () => {
    setOpenConfirmModal(null);
  };

  const classes = useStyles();
  const { rtlActive } = props;

  const dropdownItem = classNames(classes.dropdownItem, classes.primaryHover, {
    [classes.dropdownItemRTL]: rtlActive,
  });
  const wrapper = classNames({
    [classes.wrapperRTL]: rtlActive,
  });
  const managerClasses = classNames({
    [classes.managerClasses]: true,
  });

  return (
    <>
      {openConfirmModal}
      <CustomDrawerConfigs
        clientConfig={clientConfig}
        isLoadingClientConfig={isClientConfigLoading}
      />

      <CustomDrawerReport />

      <div className={wrapper}>
        {hasRealmRole(["Reports", "superUser"]) && isItemActive("reports") && (
          <div className={managerClasses}>
            <Button
              color="transparent"
              aria-label="Side Drawer"
              justIcon
              className={isLoadingReport ? classes.blinking : ""}
            >
              <Reports />
            </Button>
          </div>
        )}

        <div className={managerClasses}>
          <Button
            color="transparent"
            aria-label="Side Drawer"
            justIcon
            className={rtlActive ? classes.buttonLinkRTL : classes.buttonLink}
          >
            <NotificationPopup />
          </Button>
        </div>

        <div className={managerClasses}>
          <Tooltip title="Side Drawer">
            <Button
              color="transparent"
              aria-label="Side Drawer"
              justIcon
              onClick={() => setDrawerOpen(!drawerOpen)}
              className={rtlActive ? classes.buttonLinkRTL : classes.buttonLink}
            >
              <Menu
                className={
                  classes.headerLinksSvg +
                  " " +
                  (rtlActive
                    ? classes.links + " " + classes.linksRTL
                    : classes.links)
                }
              />
            </Button>
          </Tooltip>
        </div>

        {hasRealmRole(["Bug Reporter"]) && (
          <div className={managerClasses}>
            <Tooltip title={t("opening-calls")}>
              <Button
                color="transparent"
                aria-label={t("opening-calls")}
                justIcon
                onClick={() => setDrawerReporteOpen(!drawerReporteOpen)}
                className={
                  rtlActive ? classes.buttonLinkRTL : classes.buttonLink
                }
              >
                <BugReport
                  className={
                    classes.headerLinksSvg +
                    " " +
                    (rtlActive
                      ? classes.links + " " + classes.linksRTL
                      : classes.links)
                  }
                />
              </Button>
            </Tooltip>
          </div>
        )}

        <div className={managerClasses}>
          <Button
            color="transparent"
            aria-label="Person"
            justIcon
            aria-owns={openProfile ? "profile-menu-list" : null}
            aria-haspopup="true"
            onClick={handleClickProfile}
            className={rtlActive ? classes.buttonLinkRTL : classes.buttonLink}
            muiClasses={{
              label: rtlActive ? classes.labelRTL : "",
            }}
          >
            <Person
              className={
                classes.headerLinksSvg +
                " " +
                (rtlActive
                  ? classes.links + " " + classes.linksRTL
                  : classes.links)
              }
            />
            <Hidden mdUp implementation="css">
              <span onClick={handleClickProfile} className={classes.linkText}>
                {rtlActive ? "الملف الشخصي" : "Perfil"}
              </span>
            </Hidden>
          </Button>
          <Popper
            open={Boolean(openProfile)}
            anchorEl={openProfile}
            transition
            disablePortal
            placement="bottom"
            className={classNames({
              [classes.popperClose]: !openProfile,
              [classes.popperResponsive]: true,
              [classes.popperNav]: true,
            })}
          >
            {({ TransitionProps }) => (
              <Grow
                {...TransitionProps}
                id="profile-menu-list"
                style={{ transformOrigin: "0 0 0" }}
              >
                <Paper className={classes.dropdown}>
                  <ClickAwayListener onClickAway={handleCloseProfile}>
                    <MenuList role="menu">
                      <MenuItem
                        onClick={navigateToProfile}
                        className={dropdownItem}
                      >
                        {rtlActive ? "الملف الشخصي" : t("profile")}
                      </MenuItem>
                      <Divider light />
                      <MenuItem
                        onClick={handleOpenConfirmModal}
                        className={dropdownItem}
                      >
                        {rtlActive ? "الخروج" : t("logout")}
                      </MenuItem>
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </div>
      </div>
    </>
  );
}

HeaderLinks.propTypes = {
  rtlActive: PropTypes.bool,
};
